import { createFileRoute } from "@tanstack/react-router";
import heroImage from "@/assets/event-hero.jpg";
import heroWebp768 from "@/assets/event-hero-768.webp";
import heroWebp1376 from "@/assets/event-hero-1376.webp";
import footerArt from "@/assets/footer-art.jpg.asset.json";



import speakerJeffrey from "@/assets/speakers/jeffrey-mishlove.webp";
import speakerHelane from "@/assets/speakers/helane-wahbeh.webp";
import speakerHaroldo from "@/assets/speakers/haroldo-dutra-dias.webp";
import speakerAlexander from "@/assets/speakers/alexander-moreira-almeida.webp";
import hostDan from "@/assets/speakers/dan-assisi.webp";

const speakerPhotos = [speakerJeffrey, speakerHelane, speakerHaroldo, speakerAlexander];
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Calendar,
  MapPin,
  Languages,
  Sparkles,
  Brain,
  HeartPulse,
  Users,
  Ticket,
  Clock,
  Mail,
  ArrowRight,
  Accessibility,
  Car,
  Baby,
} from "lucide-react";
import { I18nProvider, useI18n, type Lang } from "@/lib/i18n";

const SEO_TITLE = "Well-being, Spirituality & Science Summit | Sep 12, 2026";
const SEO_DESC =
  "One-day event on science, spirituality, Spiritism & well-being. September 12, 2026 in Davenport, FL. Portuguese translation available.";
const SITE_URL = "https://spiritsciencesummit.com";

const FAQ_ITEMS: Array<{ q: string; a: string }> = [
  { q: "When is the event?", a: "Saturday, September 12, 2026." },
  { q: "Where is the event located?", a: "Tom Fellows Community Center, Davenport, Florida — in the Orlando area." },
  { q: "Will there be Portuguese translation?", a: "Yes. Simultaneous translation to Portuguese will be available throughout the entire event." },
  { q: "How do I buy tickets?", a: "Tickets can be purchased online via our official ticketing partner using the Buy Tickets button." },
  { q: "Are seats limited?", a: "Yes. Seating is limited and Early Bird tickets are released in a small allotment. Reserve early." },
  { q: "Will food or refreshments be available?", a: "Refreshment details will be confirmed closer to the event date and shared with registered attendees." },
  { q: "Can I transfer my ticket?", a: "Tickets may be transferable subject to our official terms and refund policy." },
  { q: "Who should attend this event?", a: "Anyone interested in spirituality, Spiritism, consciousness studies, mental health, healing, scientific inquiry and personal transformation." },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: SEO_TITLE },
      { name: "description", content: SEO_DESC },
      { property: "og:title", content: SEO_TITLE },
      { property: "og:description", content: SEO_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: SITE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: SEO_TITLE },
      { name: "twitter:description", content: SEO_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [
      { rel: "canonical", href: SITE_URL + "/" },
      {
        rel: "preload",
        as: "image",
        href: heroWebp768,
        imageSrcSet: `${heroWebp768} 768w, ${heroWebp1376} 1376w`,
        imageSizes: "100vw",
        fetchPriority: "high",

      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Event",
          name: "Integrating Well-being, Spirituality, Spiritism & Science",
          startDate: "2026-09-12",
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          eventStatus: "https://schema.org/EventScheduled",
          location: {
            "@type": "Place",
            name: "Tom Fellows Community Center — Davenport, Florida (Orlando Area)",
            address: "Davenport, FL, USA",
          },
          description: SEO_DESC,
          image: [heroImage],
          performer: [
            { "@type": "Person", name: "Jeffrey Mishlove, PhD" },
            { "@type": "Person", name: "Helané Wahbeh, ND, MCR" },
            { "@type": "Person", name: "Haroldo Dutra Dias, PhD" },
            { "@type": "Person", name: "Alexander Moreira-Almeida, MD, PhD" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ_ITEMS.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
    ],
  }),
  component: () => (
    <I18nProvider>
      <LandingPage />
    </I18nProvider>
  ),
});

const benefitIcons = [Sparkles, Brain, HeartPulse, Languages];

function LandingPage() {
  return (
    <div className="min-h-screen text-foreground">
      <Nav />
      <main id="main">
        <Hero />
        <Overview />
        <Audience />
        <Speakers />
        <Host />
        <Experience />
        <Agenda />
        <Tickets />
        <Translation />
        <Location />
        <FAQ />
        <Photos />
        <FinalCTA />
        <Sponsors />
        <Organizers />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}

function LangToggle({ className = "" }: { className?: string }) {
  const { lang, setLang } = useI18n();
  const Btn = ({ value, label }: { value: Lang; label: string }) => (
    <button
      type="button"
      onClick={() => setLang(value)}
      aria-pressed={lang === value}
      className={[
        "px-2.5 py-1 text-xs font-medium uppercase tracking-wider transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        lang === value
          ? "bg-gold text-primary-foreground"
          : "text-muted-foreground hover:text-foreground",
      ].join(" ")}
    >
      {label}
    </button>
  );
  return (
    <div
      className={[
        "inline-flex items-center overflow-hidden rounded-full border border-border/60 bg-card/40 backdrop-blur",
        className,
      ].join(" ")}
      role="group"
      aria-label="Language"
    >
      <Btn value="en" label="🇺🇸" />
      <Btn value="pt" label="🇧🇷" />
    </div>
  );
}

function Nav() {
  const { t } = useI18n();
  return (
    <header className="fixed top-0 z-50 w-full border-b border-border/40 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
        <a href="#top" className="flex items-center" aria-label="Home">
          <span className="font-display text-lg text-gradient-gold">Spirit Science Summit</span>
        </a>

        <nav className="hidden items-center gap-8 text-sm text-muted-foreground md:flex">
          <a href="#speakers" className="hover:text-foreground">{t.nav.speakers}</a>
          <a href="#agenda" className="hover:text-foreground">{t.nav.agenda}</a>
          <a href="#tickets" className="hover:text-foreground">{t.nav.tickets}</a>
          <a href="#photos" className="hover:text-foreground">{t.nav.photos}</a>
          <a href="#faq" className="hover:text-foreground">{t.nav.faq}</a>
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle />
          <Button asChild size="sm" className="bg-gold text-primary-foreground hover:opacity-90 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background">
            <a href="#tickets">{t.nav.getTickets}</a>
          </Button>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const { t } = useI18n();
  return (
    <section id="top" className="relative isolate overflow-hidden pt-28 pb-12">
      <div className="absolute inset-0 -z-10">
        <img
          src={heroWebp768}
          srcSet={`${heroWebp768} 768w, ${heroWebp1376} 1376w`}
          sizes="100vw"
          alt="Cosmic illustration with two human silhouettes connected by a luminous DNA helix, representing the integration of spirituality and science"
          className="h-full w-full object-cover opacity-40"
          width={1376}
          height={768}
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/70 to-background" />
        <div className="absolute inset-0 starfield opacity-60" />
      </div>

      <div className="mx-auto max-w-5xl px-6 text-center">
        <div className="mx-auto mb-8 inline-flex flex-wrap items-center justify-center gap-x-6 gap-y-2 rounded-full border border-border/60 bg-card/40 px-5 py-2 text-xs uppercase tracking-[0.2em] text-muted-foreground backdrop-blur">
          <span className="inline-flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-gold" /> {t.hero.date}</span>
          <span className="inline-flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-gold" /> {t.hero.venue}</span>
          <span className="inline-flex items-center gap-2"><Languages className="h-3.5 w-3.5 text-gold" /> {t.hero.lang}</span>
        </div>

        <h1 className="font-display text-4xl leading-[1.05] sm:text-6xl md:text-7xl">
          <span className="block text-foreground/90">{t.hero.h1a}</span>
          <span className="mt-2 block text-gradient-aurora">{t.hero.h1b}</span>
        </h1>

        <p className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          {t.hero.sub}
        </p>

        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button asChild size="lg" className="bg-gold text-primary-foreground shadow-glow hover:opacity-90 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background">
            <a href="#tickets">
              {t.hero.cta1} <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-border/60 bg-card/30 text-foreground backdrop-blur hover:bg-card/60 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <a href="#speakers">{t.hero.cta2}</a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function SectionHeader({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: React.ReactNode;
  subtitle?: string;
}) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      <p className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">{eyebrow}</p>
      <h2 className="font-display text-3xl sm:text-4xl md:text-5xl">{title}</h2>
      {subtitle ? (
        <p className="mx-auto mt-5 max-w-2xl text-muted-foreground">{subtitle}</p>
      ) : null}
    </div>
  );
}

function Overview() {
  const { t } = useI18n();
  return (
    <section id="overview" className="relative py-12">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeader
          eyebrow={t.overview.eyebrow}
          title={<>{t.overview.title}</>}
          subtitle={t.overview.subtitle}
        />
        <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4 text-gold" />
          <span>{t.overview.venueLine}</span>
        </p>

        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {t.overview.benefits.map((b, i) => {
            const Icon = benefitIcons[i] ?? Sparkles;
            return (
              <div
                key={b.title}
                className="group rounded-2xl border border-border/60 bg-card/40 p-6 backdrop-blur transition hover:border-gold/40 hover:bg-card/70"
              >
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gold/10 text-gold ring-1 ring-gold/20">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-xl">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Audience() {
  const { t } = useI18n();
  return (
    <section className="relative py-12">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <p className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">{t.audience.eyebrow}</p>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl">
              {t.audience.title}
            </h2>
            <p className="mt-5 text-muted-foreground">{t.audience.sub}</p>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {t.audience.items.map((a) => (
              <li
                key={a}
                className="flex items-start gap-3 rounded-xl border border-border/50 bg-card/40 p-4 backdrop-blur"
              >
                <Users className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                <span className="text-sm text-foreground/90">{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Speakers() {
  const { t } = useI18n();
  return (
    <section id="speakers" className="defer-render relative py-12">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeader
          eyebrow={t.speakers.eyebrow}
          title={<>{t.speakers.title}</>}
          subtitle={t.speakers.sub}
        />

        <div className="mt-16 grid gap-6 md:grid-cols-2">
          {t.speakers.items.map((s, idx) => (
            <article
              key={s.name}
              className="flex h-full flex-col rounded-2xl border border-border/60 bg-card/50 p-8 backdrop-blur shadow-elegant transition hover:border-gold/40"
            >
              <div className="mb-5 flex items-center gap-4">
                <img
                  src={speakerPhotos[idx]}
                  alt={`Portrait of ${s.name}`}
                  width={64}
                  height={64}
                  loading="lazy"
                  decoding="async"
                  className="h-16 w-16 shrink-0 rounded-full object-cover ring-1 ring-gold/30"
                />
                <div>
                  <h3 className="font-display text-2xl leading-tight">{s.name}</h3>
                  <p className="text-sm text-muted-foreground">{s.title}</p>
                </div>
              </div>
              <p className="text-sm leading-relaxed text-foreground/85">{s.bio}</p>
              <div className="mt-5 rounded-lg border border-border/40 bg-background/40 p-4">
                <p className="text-xs uppercase tracking-widest text-gold/90">{t.speakers.contributionLabel}</p>
                <p className="mt-1 text-sm text-muted-foreground">{s.contribution}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 flex items-center justify-center gap-3 rounded-xl border border-gold/40 bg-gold/10 px-6 py-5 text-center backdrop-blur">
          <Sparkles className="h-6 w-6 shrink-0 text-gold" />
          <p className="text-base font-bold text-foreground">{t.speakers.bookSigning}</p>
        </div>
      </div>
    </section>
  );
}

function Host() {
  const { t } = useI18n();
  return (
    <section id="host" className="defer-render relative py-12">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeader eyebrow={t.host.eyebrow} title={<>{t.host.name}</>} subtitle={t.host.tagline} />
        <div className="mx-auto mt-12 max-w-3xl rounded-2xl border border-border/60 bg-card/50 p-8 backdrop-blur shadow-elegant">
          <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
            <img
              src={hostDan}
              alt={`Portrait of ${t.host.name}`}
              width={80}
              height={80}
              loading="lazy"
              decoding="async"
              className="h-20 w-20 shrink-0 rounded-full object-cover ring-1 ring-gold/30"
            />
            <div className="space-y-4 text-sm leading-relaxed text-foreground/85">
              <p>{t.host.bio}</p>
              <p>{t.host.bio2}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Experience() {
  const { t } = useI18n();
  return (
    <section className="defer-render relative py-12">
      <div className="mx-auto max-w-7xl px-6">
        <div className="overflow-hidden rounded-3xl border border-border/60 bg-card/40 p-10 backdrop-blur md:p-14">
          <SectionHeader
            eyebrow={t.experience.eyebrow}
            title={<>{t.experience.title}</>}
          />
          <div className="mx-auto mt-12 grid max-w-4xl gap-3 sm:grid-cols-2">
            {t.experience.items.map((i) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-xl border border-border/50 bg-background/40 p-4"
              >
                <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-gold shadow-glow" />
                <p className="text-sm text-foreground/90">{i}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Agenda() {
  const { t } = useI18n();
  return (
    <section id="agenda" className="defer-render relative py-12">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeader
          eyebrow={t.agenda.eyebrow}
          title={<>{t.agenda.title}</>}
          subtitle={t.agenda.sub}
        />

        <div className="mx-auto mt-14 max-w-3xl rounded-2xl border border-border/60 bg-card/40 p-2 backdrop-blur">
          <ol className="divide-y divide-border/50">
            {t.agenda.items.map((item, idx) => (
              <li
                key={idx}
                className="flex items-start gap-4 px-5 py-4"
              >
                <Clock className="mt-1 h-4 w-4 shrink-0 text-gold" />
                <div className="flex flex-col gap-1">
                  <span className="text-foreground/90">{item.label}</span>
                  <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                    {item.time}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function Tickets() {
  const { t } = useI18n();
  return (
    <section id="tickets" className="defer-render relative py-12">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeader
          eyebrow={t.tickets.eyebrow}
          title={<>{t.tickets.title}</>}
          subtitle={t.tickets.sub}
        />

        <div className="mx-auto mt-16 flex max-w-4xl flex-wrap items-stretch justify-center gap-6">
          {t.tickets.list.map((tk) => (
            <div
              key={tk.name}
              className={[
                "relative flex w-full flex-col rounded-2xl border p-8 backdrop-blur transition sm:w-[calc(50%-0.75rem)]",
                tk.featured
                  ? "border-gold/50 bg-card/70 shadow-glow"
                  : "border-border/60 bg-card/40 hover:border-gold/30",
              ].join(" ")}
            >
              <h3 className="text-center font-display text-2xl">{tk.name}</h3>
              <p className="mt-3 text-center font-display text-4xl text-gradient-gold">{tk.price}</p>
              <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
                {tk.perks.map((p) => {
                  const isLunchPerk = /lunch|meal|drinks|dessert|vegetarian|refeição|bebidas|sobremesa|vegetariana/i.test(p);
                  return (
                    <li key={p} className={["flex items-start gap-2", isLunchPerk ? "font-semibold text-foreground/90" : ""].join(" ")}>
                      {isLunchPerk ? (
                        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                      ) : (
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                      )}
                      {p}
                    </li>
                  );
                })}
              </ul>
              <Button
                asChild
                className="mt-auto w-full bg-gold text-primary-foreground hover:opacity-90 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <a href="https://portal.mypocketpay.com/new/event/UDE4VFA4RDdlYg==" target="_blank" rel="noopener noreferrer">
                  <Ticket className="mr-2 h-4 w-4" />
                  {t.tickets.cta}
                </a>
              </Button>
            </div>
          ))}

          <div className="relative flex w-full flex-col items-center rounded-2xl border border-gold/40 bg-card/50 p-8 backdrop-blur transition hover:border-gold/60 sm:w-[calc(50%-0.75rem)]">
            <div className="flex items-center justify-center gap-2">
              <Baby className="h-6 w-6 text-gold" />
              <h3 className="font-display text-2xl">{t.tickets.daycare.name}</h3>
            </div>
            <p className="mt-3 text-center font-display text-4xl text-gradient-gold">{t.tickets.daycare.price}</p>
            <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
              {t.tickets.daycare.perks.map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                  {p}
                </li>
              ))}
            </ul>
            <Button
              asChild
              className="mt-auto w-full bg-gold text-primary-foreground hover:opacity-90 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <a href="https://portal.mypocketpay.com/new/event/UDE4VFA4RDdlYg==" target="_blank" rel="noopener noreferrer">
                <Ticket className="mr-2 h-4 w-4" />
                {t.tickets.cta}
              </a>
            </Button>
          </div>
        </div>

        <div className="mx-auto mt-16 max-w-3xl text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">{t.tickets.liveStream.eyebrow}</p>
          <h3 className="mt-4 font-display text-2xl">{t.tickets.liveStream.title}</h3>
          {t.tickets.liveStream.sub ? (
            <p className="mt-2 text-muted-foreground">{t.tickets.liveStream.sub}</p>
          ) : null}
          <div className="mt-8 flex flex-wrap justify-center gap-6">
            {t.tickets.liveStream.list.map((tk) => (
              <div
                key={tk.name}
                className="flex w-full max-w-md flex-col rounded-2xl border border-border/60 bg-card/40 p-6 backdrop-blur transition hover:border-gold/30"
              >
                <h4 className="font-display text-xl">{tk.name}</h4>
                <p className="mt-3 font-display text-3xl text-gradient-gold">{tk.price}</p>
                <Button
                  asChild
                  className="mt-6 w-full bg-gold text-primary-foreground hover:opacity-90 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <a href="https://portal.mypocketpay.com/new/event/UDE4VFA4RDdlYg==" target="_blank" rel="noopener noreferrer">
                    <Ticket className="mr-2 h-4 w-4" />
                    {t.tickets.cta}
                  </a>
                </Button>
              </div>
            ))}
          </div>
        </div>


        <p className="mt-8 text-center text-xs uppercase tracking-[0.25em] text-muted-foreground">
          {t.tickets.note}
        </p>
      </div>
    </section>
  );
}

function Translation() {
  const { t } = useI18n();
  return (
    <section className="defer-render relative py-12">
      <div className="mx-auto max-w-5xl px-6">
        <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card/70 to-background/40 p-10 text-center backdrop-blur md:p-16">
          <div className="absolute inset-0 starfield opacity-40" />
          <div className="relative">
            <Languages className="mx-auto h-10 w-10 text-gold" />
            <h2 className="mt-6 font-display text-3xl sm:text-4xl">
              {t.translation.titleA}
              <span className="text-gradient-gold">{t.translation.titleB}</span>
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-muted-foreground">
              {t.translation.body}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Location() {
  const { t } = useI18n();
  const mapsQuery = encodeURIComponent(
    "Tom Fellows Community Center, 207 North Boulevard West, Davenport, FL 33837",
  );
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${mapsQuery}`;
  const embedUrl = `https://www.google.com/maps?q=${mapsQuery}&output=embed`;
  return (
    <section id="location" className="defer-render relative py-12">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeader
          eyebrow={t.location.eyebrow}
          title={<>{t.location.title}</>}
        />
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-border/60 bg-card/40 p-8 backdrop-blur">
            <MapPin className="h-6 w-6 text-gold" />
            <h3 className="mt-4 font-display text-2xl">{t.location.venueHeading}</h3>
            <dl className="mt-4 space-y-3 text-sm">
              <Row label={t.location.venueLabel} value={t.location.venueValue} />
              <Row label={t.location.addressLabel} value={t.location.addressValue} />
              <Row label={t.location.parkingLabel} value={t.location.parkingValue} />
            </dl>
            <Button
              asChild
              className="mt-6 bg-gold text-primary-foreground hover:opacity-90 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <a href={directionsUrl} target="_blank" rel="noopener noreferrer">
                <MapPin className="mr-2 h-4 w-4" />
                {t.location.directionsLabel}
              </a>
            </Button>
          </div>
          <div className="flex flex-col gap-6">
            <div className="rounded-2xl border border-border/60 bg-card/40 p-8 backdrop-blur">
              <Car className="h-6 w-6 text-gold" />
              <h3 className="mt-4 font-display text-2xl">{t.location.parkingLabel}</h3>
              <p className="mt-4 text-sm text-muted-foreground">{t.location.parkingNote}</p>
            </div>
            <div className="rounded-2xl border border-border/60 bg-card/40 p-8 backdrop-blur">
              <Accessibility className="h-6 w-6 text-gold" />
              <h3 className="mt-4 font-display text-2xl">{t.location.accessibilityHeading}</h3>
              <p className="mt-4 text-sm text-muted-foreground">{t.location.accessibilityNote}</p>
            </div>
          </div>
        </div>
        <div className="mt-8 overflow-hidden rounded-2xl border border-border/60 bg-card/40">
          <iframe
            title="Tom Fellows Community Center map"
            src={embedUrl}
            className="h-[360px] w-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-6 border-b border-border/40 pb-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right text-foreground/90">{value}</dd>
    </div>
  );
}

function FAQ() {
  const { t } = useI18n();
  return (
    <section id="faq" className="defer-render relative py-12">
      <div className="mx-auto max-w-3xl px-6">
        <SectionHeader eyebrow={t.faq.eyebrow} title={<>{t.faq.title}</>} />
        <Accordion type="single" collapsible className="mt-12">
          {t.faq.items.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`item-${i}`}
              className="border-border/50"
            >
              <AccordionTrigger className="text-left font-display text-lg hover:text-gold">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function FinalCTA() {
  const { t } = useI18n();
  return (
    <section className="defer-render relative py-28">
      <div className="mx-auto max-w-4xl px-6 text-center">
        <div className="absolute inset-0 -z-10 starfield opacity-50" />
        <h2 className="font-display text-4xl leading-tight sm:text-5xl md:text-6xl">
          {t.finalCta.h2a}
          <span className="text-gradient-aurora">{t.finalCta.h2b}</span>
          {t.finalCta.h2c}
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-muted-foreground">
          {t.finalCta.sub}
        </p>
        <div className="mt-10">
          <Button asChild size="lg" className="bg-gold text-primary-foreground shadow-glow hover:opacity-90 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background">
            <a href="#tickets">
              {t.finalCta.cta} <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function Photos() {
  const { t } = useI18n();
  return (
    <section id="photos" className="defer-render py-20">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <p className="text-xs uppercase tracking-[0.25em] text-gold">{t.photos.eyebrow}</p>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl">{t.photos.title}</h2>
        <p className="mx-auto mt-4 max-w-xl text-muted-foreground">{t.photos.subtitle}</p>
        <div className="mt-8">
          <Button
            asChild
            size="lg"
            className="bg-gold text-primary-foreground hover:opacity-90 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <a
              href="https://moreatephotos.pixieset.com/spiritsciencesummit2026/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t.photos.ctaAria}
            >
              {t.photos.cta} <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}



function Footer() {
  const { t } = useI18n();
  return (
    <footer className="border-t border-border/50 bg-background/60 py-10 backdrop-blur">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-6 text-center md:grid-cols-4">
        <div>
          <p className="font-display text-xl">
            <span className="text-gradient-gold">{t.footer.brand}</span>
          </p>
          <p className="mt-3 text-sm text-muted-foreground">{t.footer.tagline}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-gold">{t.footer.contact}</p>
          <a
            href="mailto:info@spiritsciencesummit.com"
            className="mt-3 inline-flex items-center justify-center gap-2 text-sm text-gold underline underline-offset-4 hover:text-foreground"
          >
            <Mail className="h-4 w-4" /> {t.footer.contactEmail}
          </a>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-gold">{t.footer.legal}</p>
          <ul className="mt-3 space-y-2 text-sm">
            <li><a href="/terms" className="text-gold underline underline-offset-4 hover:text-foreground">{t.footer.terms}</a></li>
            <li><a href="/refund" className="text-gold underline underline-offset-4 hover:text-foreground">{t.footer.refund}</a></li>
            <li><a href="/privacy" className="text-gold underline underline-offset-4 hover:text-foreground">{t.footer.privacy}</a></li>
          </ul>
        </div>

        <div className="flex items-center justify-center">
          <img
            src={footerArt.url}
            alt="Two luminous figures connected by a DNA helix, symbolizing the union of science and spirituality"
            className="max-h-32 w-full rounded-xl object-contain opacity-90"
            width={320}
            height={320}
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
      <div className="mx-auto mt-8 max-w-7xl px-6 text-center text-xs text-muted-foreground">
        {t.footer.copyright}
      </div>
    </footer>
  );
}

import spoFon from "@/assets/sponsors/fon-law-immigration.jpg.asset.json";
import spoMaison from "@/assets/sponsors/maison-aurea.jpg.asset.json";
import spoLigia from "@/assets/sponsors/ligia-coelho.jpg.asset.json";
import spoOzone from "@/assets/sponsors/ozone-harbor.jpg.asset.json";
import spoOxyn from "@/assets/sponsors/oxyn-aesthetics.jpg.asset.json";
import spoOrnna from "@/assets/sponsors/ornna-foods.jpg.asset.json";
import spoStone from "@/assets/sponsors/stone-and-surface.jpg.asset.json";
import spoSpazio from "@/assets/sponsors/spazio-prime-group.jpg.asset.json";
import spoAssureline from "@/assets/sponsors/assureline-insurance.jpg.asset.json";
import spoSunny from "@/assets/sponsors/sunny-side-cafe.jpeg.asset.json";
import spoQueen from "@/assets/sponsors/queen-pilates.png.asset.json";
import org1 from "@/assets/logos/1.png.asset.json";
import org2 from "@/assets/logos/2.png.asset.json";
import org3 from "@/assets/logos/3.png.asset.json";
import plenitude from "@/assets/logos/plenitude.png.asset.json";




const SPONSORS: { src: string; name: string; href?: string }[] = [
  { src: spoFon.url, name: "Fon Law Immigration", href: "https://www.fonlawfirm.com" },
  { src: spoMaison.url, name: "Maison Aurea", href: "https://www.instagram.com/maisonaurea.lifeart" },
  { src: spoLigia.url, name: "Ligia Coelho Realtor", href: "https://www.instagram.com/coelholigiafrealtor/" },
  { src: spoOzone.url, name: "Ozone Harbor", href: "https://www.instagram.com/ozoneharbor/" },
  { src: spoOxyn.url, name: "Oxyn Aesthetics", href: "https://www.instagram.com/oxynaesthetics/" },
  { src: spoOrnna.url, name: "Ornna Foods", href: "https://www.ornnafoods.com" },
  { src: spoStone.url, name: "Stone & Surface", href: "https://www.stoneandsurface.com" },
  { src: spoSpazio.url, name: "Spazio Prime Group", href: "https://linktr.ee/Spazioprimegroup" },
  { src: spoAssureline.url, name: "Assureline Insurance", href: "https://assurelineinsurance.com/en/" },
  { src: spoSunny.url, name: "Sunny Side Cafe", href: "https://sunnysidecafe.com/" },
  { src: spoQueen.url, name: "Queen Pilates Studios", href: "https://www.queenpilatesstudios.com/" },
];



function Sponsors() {
  const { lang } = useI18n();
  const spoTitle = lang === "pt" ? "Patrocinadores" : "Sponsors";
  const newTabLabel = lang === "pt" ? "abre em uma nova aba" : "opens in a new tab";

  return (
    <section id="sponsors" className="defer-render relative py-10 border-t border-border/40">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">{spoTitle}</p>
          <div className="mt-8 grid grid-cols-2 place-items-center justify-center gap-6 md:grid-cols-4">
            {SPONSORS.map((s, i) => {
              const inner = (
                <img
                  src={s.src}
                  alt={s.name}
                  className="max-h-32 max-w-64 object-contain object-center opacity-90 transition hover:opacity-100"
                  width={256}
                  height={128}
                  loading="lazy"
                  decoding="async"
                />
              );
              const className =
                "flex h-40 w-full max-w-72 items-center justify-center rounded-2xl border border-border/40 bg-background/40 p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background";
              return s.href ? (
                <a
                  key={i}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={className}
                  aria-label={`${s.name} (${newTabLabel})`}
                >
                  {inner}
                </a>
              ) : (
                <div key={i} className={className}>
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

const ORGANIZERS: { src: string; name: string; href: string }[] = [
  { src: org1.url, name: "God, Christ and Charity Spiritist Center", href: "https://gccss.org/" },
  { src: org2.url, name: "Emmanuel Spiritist Center", href: "https://emmanuelorlando.com/" },
  { src: org3.url, name: "Peace & Knowledge", href: "https://www.spiritisminorlando.com/" },
  { src: plenitude.url, name: "Plenitude Spiritist Society", href: "https://www.plenitudess.org/" },
];

function Organizers() {
  const { lang } = useI18n();
  const orgTitle = lang === "pt" ? "Organizado por" : "Organized by";
  const newTabLabel = lang === "pt" ? "abre em uma nova aba" : "opens in a new tab";

  return (
    <section id="organizers" className="defer-render relative py-10 border-t border-border/40">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">{orgTitle}</p>
          <div className="mt-8 grid grid-cols-2 place-items-center justify-center gap-6 md:grid-cols-4">
            {ORGANIZERS.map((o, i) => {
              const inner = (
                <img
                  src={o.src}
                  alt={o.name}
                  className="max-h-32 max-w-52 object-contain object-center opacity-90 transition hover:opacity-100"
                  width={208}
                  height={128}
                  loading="lazy"
                  decoding="async"
                />
              );
              const className =
                "flex h-40 w-full max-w-60 items-center justify-center rounded-2xl border border-border/40 bg-background/40 p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background";
              return (
                <a
                  key={i}
                  href={o.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={className}
                  aria-label={`${o.name} (${newTabLabel})`}
                >
                  {inner}
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatsAppButton() {
  const { t } = useI18n();
  const phone = "16197503049";
  const message = encodeURIComponent(
    "Hi! I'd like to know more about the Spirit Science Summit."
  );
  return (
    <a
      href={`https://wa.me/${phone}?text=${message}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t.footer.whatsappAria}
      className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 rounded-full bg-whatsapp px-4 py-3 text-white shadow-lg transition hover:scale-105 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="h-6 w-6"
        aria-hidden="true"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
      <span className="text-sm font-semibold">{t.footer.whatsapp}</span>
    </a>
  );
}




